import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to log auth activity (non-blocking)
// Only calls the edge function if a valid access token is provided
const logAuthActivity = async (eventType: string, accessToken: string | undefined, metadata: Record<string, unknown> = {}) => {
  // Guard: skip if no token (prevents 401s when session is missing/stale)
  if (!accessToken) {
    console.debug('Skipping auth activity log: no access token');
    return;
  }
  
  try {
    await supabase.functions.invoke('log-auth-activity', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: { event_type: eventType, metadata },
    });
  } catch (error) {
    // Silent fail - don't block auth flow for logging
    console.warn('Failed to log auth activity:', error);
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Track sessions we've already logged to prevent duplicate logs
  const loggedSessionsRef = useRef<Set<string>>(new Set());
  // Track if we're in the process of signing out
  const isSigningOutRef = useRef(false);

  useEffect(() => {
    // Auto-accept pending admin invites for the user
    const autoAcceptAdminInvite = async (userId: string, userEmail: string) => {
      try {
        // Check for pending invites matching this email
        const { data: invite, error: inviteError } = await supabase
          .from('admin_invites')
          .select('id, role, email')
          .eq('email', userEmail.toLowerCase())
          .is('accepted_at', null)
          .gt('expires_at', new Date().toISOString())
          .maybeSingle();

        if (inviteError || !invite) return;

        // Check if user already has a role
        const { data: existingRole } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();

        if (existingRole) {
          // User already has a role, just mark invite as accepted
          await supabase
            .from('admin_invites')
            .update({ accepted_at: new Date().toISOString() })
            .eq('id', invite.id);
          return;
        }

        // Create user_roles entry
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: userId,
            role: invite.role,
            email: userEmail.toLowerCase(),
          });

        if (!roleError) {
          // Mark invite as accepted
          await supabase
            .from('admin_invites')
            .update({ accepted_at: new Date().toISOString() })
            .eq('id', invite.id);
        }
      } catch (err) {
        console.error('Error auto-accepting admin invite:', err);
      }
    };

    // Doctor invite acceptance is now handled by the accept-doctor-invite edge function
    // called from DoctorSignup.tsx — no client-side writes needed here.

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Log sign-in events (only for new sessions we haven't logged)
        if (event === 'SIGNED_IN' && session?.access_token) {
          const sessionId = session.access_token.slice(-20); // Use last 20 chars as ID
          
          if (!loggedSessionsRef.current.has(sessionId)) {
            loggedSessionsRef.current.add(sessionId);

            // Defer until browser is idle so this never blocks paint / redirect
            const ric = (window as any).requestIdleCallback as
              | ((cb: () => void, opts?: { timeout: number }) => number)
              | undefined;
            const fire = () => {
              logAuthActivity('sign_in', session.access_token, {
                provider: session.user?.app_metadata?.provider || 'email',
              });
            };
            if (ric) ric(fire, { timeout: 3000 });
            else setTimeout(fire, 250);
          }

          // Auto-accept pending admin invites
          if (session?.user?.email) {
            const ric = (window as any).requestIdleCallback as
              | ((cb: () => void, opts?: { timeout: number }) => number)
              | undefined;
            const fire = () => {
              autoAcceptAdminInvite(session.user.id, session.user.email!);
            };
            if (ric) ric(fire, { timeout: 3000 });
            else setTimeout(fire, 250);
          }
        }

        // Log sign-out events (triggered by our signOut function)
        // We don't log here because by the time SIGNED_OUT fires, the token is gone
        // Instead, we log in the signOut function before calling supabase.auth.signOut()
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      // Fire-and-forget security signal (never blocks the UI)
      supabase.functions
        .invoke('security-log-event', {
          body: { event_type: 'login_failed', email, details: { reason: error.message } },
        })
        .catch(() => {});
    }
    return { error };
  };


  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signOut = async () => {
    // Log the sign-out BEFORE clearing the session
    if (session?.access_token && !isSigningOutRef.current) {
      isSigningOutRef.current = true;
      try {
        await logAuthActivity('sign_out', session.access_token);
      } catch (e) {
        // Silent fail
      }
    }
    
    // Clear the logged sessions tracking
    loggedSessionsRef.current.clear();
    isSigningOutRef.current = false;
    
    try {
      await supabase.auth.signOut();
    } catch (error: any) {
      // If logout fails (e.g., session already invalidated), still clear local state
      console.warn('Sign out error (clearing local state anyway):', error?.message);
    }
    
    // Always clear local state regardless of server response
    // This handles cases where the session was already invalidated server-side
    setUser(null);
    setSession(null);
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    // Log password reset activity and sync to Revive (non-blocking)
    if (!error) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          // Log the password reset
          logAuthActivity('password_reset', session.access_token);
          
          // Sync password to Revive
          supabase.functions.invoke('revive-sync-password', {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
            body: { password: newPassword },
          }).then(({ error: syncError }) => {
            if (syncError) {
              console.warn('Revive password sync failed:', syncError);
            }
          });
        }
      });
    }
    
    return { error };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
